import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Competition, type CompetitionRules } from "../lib/api";
import { analytics, AnalyticsEvent } from "../lib/analytics";
import { trackApiError } from "../lib/analytics/errorTracking";
import { LazyPromoSlot } from "../lib/promo/LazyPromoSlot";
import {
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  Modal,
  PageHeader,
  ProgressBar,
  Select,
  Skeleton,
  Textarea,
  cx,
} from "../components/ui";

const DASHBOARD_AD_SLOT = import.meta.env.VITE_ADSENSE_SLOT_DASHBOARD ?? "";

/**
 * Competitions are the only organising unit — there's no container above them. You create one,
 * choose its league and rules, and share its invite code.
 */
export function DashboardPage() {
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  const { data: competitions, isLoading } = useQuery({
    queryKey: ["competitions"],
    queryFn: () => api.competitions.list(),
  });

  const openCreate = () => {
    analytics.track(AnalyticsEvent.CompetitionCreateOpened);
    setCreating(true);
  };
  const openJoin = () => {
    analytics.track(AnalyticsEvent.CompetitionJoinOpened);
    setJoining(true);
  };

  const alive = competitions?.filter((c) => c.status === "running").length ?? 0;

  return (
    <>
      <PageHeader
        eyebrow="Last man standing"
        title="Your competitions"
        subtitle={
          competitions?.length
            ? `${competitions.length} ${competitions.length === 1 ? "competition" : "competitions"}${alive ? ` · ${alive} running` : ""}`
            : "Start one and share the code, or join with a code someone sent you."
        }
        actions={
          <>
            <Button variant="secondary" onClick={openJoin}>
              <Icon name="ticket" className="size-4" /> Join
            </Button>
            <Button onClick={openCreate}>
              <Icon name="plus" className="size-4" /> New
            </Button>
          </>
        }
      />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      ) : !competitions?.length ? (
        <EmptyState
          icon={<Icon name="ball" className="size-7" />}
          title="No competitions yet"
          description="Create one for your mates — you pick the league and the rules — or join an existing one with its six-character code."
          action={
            <div className="flex gap-2">
              <Button variant="secondary" onClick={openJoin}>
                <Icon name="ticket" className="size-4" /> Join
              </Button>
              <Button onClick={openCreate}>
                <Icon name="plus" className="size-4" /> New
              </Button>
            </div>
          }
        />
      ) : (
        <div className="stagger grid gap-4 sm:grid-cols-2 sm:gap-5">
          {competitions.map((competition) => (
            <CompetitionCard key={competition.id} competition={competition} />
          ))}
        </div>
      )}

      {/* Bottom of the page, well below anything a player came here to do — never between the
          header actions and the competition list. */}
      {Boolean(competitions?.length) && DASHBOARD_AD_SLOT && (
        <LazyPromoSlot slotId={DASHBOARD_AD_SLOT} className="mt-10" />
      )}

      <CreateCompetitionModal open={creating} onClose={() => setCreating(false)} />
      <JoinModal open={joining} onClose={() => setJoining(false)} />
    </>
  );
}

function CompetitionCard({ competition }: { competition: Competition }) {
  const finished = competition.status === "complete";

  return (
    <Link
      to={`/c/${competition.id}`}
      className="card-interactive group relative block overflow-hidden p-5 sm:p-6"
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-70"
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="display-title truncate text-2xl text-fg">{competition.name}</h2>
          <p className="mt-1 truncate text-sm text-fg-subtle">
            {competition.league.name} · {competition.season.label}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
          {competition.role === "admin" && <Badge tone="info">Admin</Badge>}
          <Badge tone={competition.status === "running" ? "alive" : "neutral"}>
            {competition.status}
          </Badge>
        </div>
      </div>

      <div className="mt-5 flex items-end justify-between gap-4">
        <div>
          <span className="tabular font-display text-[2.75rem] font-bold leading-none text-accent-text">
            {competition.playersAlive}
          </span>
          <span className="ml-2 text-sm text-fg-subtle">
            of {competition.playersTotal} still standing
          </span>
        </div>
        {competition.currentRoundNumber != null && (
          <div className="shrink-0 text-right">
            <p className="label">Round</p>
            <p className="tabular font-display text-2xl font-bold leading-none text-fg">
              {competition.currentRoundNumber}
            </p>
          </div>
        )}
      </div>

      {competition.playersTotal > 0 && (
        <ProgressBar
          className="mt-4"
          value={competition.playersAlive}
          max={competition.playersTotal}
          label={`${competition.playersAlive} of ${competition.playersTotal} still standing`}
        />
      )}

      {competition.winner && (
        <p className="mt-4 flex items-center gap-2 border-t border-line pt-3.5 text-sm text-accent-text">
          <Icon name="trophy" className="size-4" />
          <span className="font-semibold text-fg">{competition.winner.displayName}</span> won it
        </p>
      )}

      {competition.sharedWinners.length > 0 && (
        <p className="mt-4 flex items-center gap-2 border-t border-line pt-3.5 text-sm text-warn-text">
          <Icon name="trophy" className="size-4" />
          <span className="truncate">
            <span className="font-semibold text-fg">
              {competition.sharedWinners.map((winner) => winner.displayName).join(", ")}
            </span>{" "}
            shared it
          </span>
        </p>
      )}

      {competition.inviteCode && !finished && (
        <div className="mt-4 flex items-center gap-2.5 border-t border-line pt-3.5">
          <span className="label">Invite code</span>
          <code className="tabular rounded-lg bg-accent-soft px-2 py-1 text-sm font-bold tracking-[0.18em] text-accent-text">
            {competition.inviteCode}
          </code>
        </div>
      )}
    </Link>
  );
}

/* Create ------------------------------------------------------------------- */

const DEFAULTS = {
  lives: 0,
  drawCountsAs: "loss" as CompetitionRules["elimination"]["drawCountsAs"],
  missedPick: "eliminate" as CompetitionRules["elimination"]["missedPick"],
  deadlineOffsetMinutes: 0,
  reveal: "after_deadline" as CompetitionRules["picks"]["reveal"],
  teamReuse: "never" as CompetitionRules["picks"]["teamReuse"],
  teamPlaysTwice: "first_fixture" as CompetitionRules["picks"]["teamPlaysTwice"],
  wipeout: "full_reset" as CompetitionRules["wipeout"],
  postponedFixture: "void_survive" as CompetitionRules["postponedFixture"],
};

function CreateCompetitionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<"basics" | "rules">("basics");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [leagueId, setLeagueId] = useState("");
  const [rules, setRules] = useState(DEFAULTS);

  const set = <K extends keyof typeof DEFAULTS>(key: K, value: (typeof DEFAULTS)[K]) =>
    setRules((current) => ({ ...current, [key]: value }));

  const { data: leagues } = useQuery({
    queryKey: ["leagues"],
    queryFn: () => api.leagues.list(),
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.competitions.create({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        leagueId,
        rules: {
          elimination: {
            lives: rules.lives,
            drawCountsAs: rules.drawCountsAs,
            missedPick: rules.missedPick,
          },
          picks: {
            deadlineOffsetMinutes: rules.deadlineOffsetMinutes,
            reveal: rules.reveal,
            teamReuse: rules.teamReuse,
            teamPlaysTwice: rules.teamPlaysTwice,
          },
          wipeout: rules.wipeout,
          postponedFixture: rules.postponedFixture,
        },
      }),
    onSuccess: (competition) => {
      analytics.track(AnalyticsEvent.CompetitionCreated, {
        competitionId: competition.id,
        leagueId,
      });
      queryClient.invalidateQueries({ queryKey: ["competitions"] });
      close();
    },
    onError: (error) => trackApiError(error, "create_competition"),
  });

  function close() {
    setStep("basics");
    setName("");
    setDescription("");
    setLeagueId("");
    setRules(DEFAULTS);
    mutation.reset();
    onClose();
  }

  const canContinue = name.trim().length >= 2 && leagueId !== "";

  return (
    <Modal
      open={open}
      onClose={close}
      title="New competition"
      description={
        step === "basics"
          ? "Name it and choose the league you'll be picking from."
          : "Set the rules. You can change these until the first pick is made."
      }
      width="max-w-xl"
      footer={
        <div className="flex gap-2">
          {step === "rules" ? (
            <>
              <Button variant="ghost" onClick={() => setStep("basics")}>
                <Icon name="arrowLeft" className="size-4" /> Back
              </Button>
              <Button
                className="flex-1"
                loading={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                Create competition
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button className="flex-1" disabled={!canContinue} onClick={() => setStep("rules")}>
                Continue <Icon name="arrowRight" className="size-4" />
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-5">
        <StepDots active={step === "basics" ? 0 : 1} labels={["Basics", "Rules"]} />

        {step === "basics" ? (
          <div className="space-y-4">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Red Lion 26/27"
              required
              minLength={2}
              maxLength={80}
              autoFocus
            />

            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Sunday league lads"
              maxLength={280}
              rows={2}
              hint="Optional."
            />

            <Select
              label="League"
              value={leagueId}
              onChange={(e) => setLeagueId(e.target.value)}
              required
              hint="The team pool is this league's clubs. A bigger league means longer before your pool runs dry."
            >
              <option value="">Choose a league…</option>
              {leagues?.map((league) => (
                <option key={league.id} value={league.id} disabled={!league.currentSeason}>
                  {league.name}
                  {league.currentSeason ? ` — ${league.currentSeason.label}` : " (no season yet)"}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <div className="space-y-5">
            <RuleGroup title="Elimination">
              <Select
                label="Lives"
                value={rules.lives}
                onChange={(e) => set("lives", Number(e.target.value))}
              >
                <option value={0}>Sudden death (0)</option>
                <option value={1}>1 life</option>
                <option value={2}>2 lives</option>
                <option value={3}>3 lives</option>
              </Select>
              <Select
                label="A draw counts as"
                value={rules.drawCountsAs}
                onChange={(e) => set("drawCountsAs", e.target.value as typeof rules.drawCountsAs)}
              >
                <option value="loss">A loss — you're out</option>
                <option value="win">A win — you survive</option>
                <option value="void">Void — survive, team returns</option>
              </Select>
              <Select
                label="No pick by the deadline"
                value={rules.missedPick}
                onChange={(e) => set("missedPick", e.target.value as typeof rules.missedPick)}
                hint={
                  rules.missedPick === "eliminate" && rules.lives > 0
                    ? "Out on the spot — lives don't cover a missed deadline."
                    : undefined
                }
              >
                <option value="eliminate">Eliminated</option>
                <option value="auto_assign">Pick a team for them</option>
                <option value="carry_forward">Carry their last pick forward</option>
              </Select>
              <Select
                label="If their game is postponed"
                value={rules.postponedFixture}
                onChange={(e) =>
                  set("postponedFixture", e.target.value as typeof rules.postponedFixture)
                }
              >
                <option value="void_survive">Survive; team returns to the pool</option>
                <option value="loss">Counts as a loss</option>
                <option value="hold_round">Hold the round until it's played</option>
              </Select>
            </RuleGroup>

            <RuleGroup title="Picks">
              <Select
                label="Picks lock"
                value={rules.deadlineOffsetMinutes}
                onChange={(e) => set("deadlineOffsetMinutes", Number(e.target.value))}
              >
                <option value={0}>At first kick-off</option>
                <option value={60}>1 hour before</option>
                <option value={720}>12 hours before</option>
                <option value={1440}>24 hours before</option>
              </Select>
              <Select
                label="Show everyone's picks"
                value={rules.reveal}
                onChange={(e) => set("reveal", e.target.value as typeof rules.reveal)}
              >
                <option value="after_deadline">After the deadline</option>
                <option value="immediately">Straight away</option>
                <option value="after_settlement">Once the round is settled</option>
              </Select>
              <Select
                label="Team reuse"
                value={rules.teamReuse}
                onChange={(e) => set("teamReuse", e.target.value as typeof rules.teamReuse)}
              >
                <option value="never">Never reuse a team</option>
                <option value="reset_on_exhaustion">Reset after every team is used</option>
                <option value="unlimited">Allow teams every round</option>
              </Select>
              <Select
                label="If a team plays twice"
                value={rules.teamPlaysTwice}
                onChange={(e) =>
                  set("teamPlaysTwice", e.target.value as typeof rules.teamPlaysTwice)
                }
              >
                <option value="first_fixture">First fixture counts</option>
                <option value="must_win_both">They must win both</option>
              </Select>
            </RuleGroup>

            <RuleGroup title="Deadlock">
              <div className="sm:col-span-2">
                <Select
                  label="If everyone goes out in the same round"
                  value={rules.wipeout}
                  onChange={(e) => set("wipeout", e.target.value as typeof rules.wipeout)}
                  hint="It happens more often than you'd think once only a few players are left. Without a rule here, the competition would simply stop with nobody having won."
                >
                  <option value="full_reset">Everyone returns and team pools reset</option>
                  <option value="revive_round">Everyone returns — used teams stay used</option>
                  <option value="split">Prize splits between them — competition ends</option>
                  <option value="no_winner">Competition ends with no winner</option>
                </Select>
              </div>
            </RuleGroup>

            <p className="text-[13px] leading-relaxed text-fg-subtle">
              You can change these until the first pick is made or round 1 locks. After that
              they're fixed — so nobody can move the goalposts once results are in. New players
              can join up until round 1 locks too, then entry closes automatically.
            </p>
          </div>
        )}

        {mutation.error && (
          <ErrorNote>
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : "Couldn't create the competition."}
          </ErrorNote>
        )}
      </div>
    </Modal>
  );
}

function RuleGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="surface-soft p-4">
      <p className="label mb-3">{title}</p>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function StepDots({ active, labels }: { active: number; labels: string[] }) {
  return (
    <div className="flex items-center gap-2">
      {labels.map((label, index) => (
        <div key={label} className="flex flex-1 items-center gap-2">
          <span
            className={cx(
              "grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-bold transition-colors",
              index <= active ? "bg-accent text-accent-fg" : "bg-tint-strong text-fg-faint",
            )}
          >
            {index + 1}
          </span>
          <span
            className={cx(
              "text-[13px] font-medium",
              index <= active ? "text-fg" : "text-fg-faint",
            )}
          >
            {label}
          </span>
          {index < labels.length - 1 && <span className="h-px flex-1 bg-line" />}
        </div>
      ))}
    </div>
  );
}

/* Join --------------------------------------------------------------------- */

function JoinModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");

  const mutation = useMutation({
    mutationFn: () => api.competitions.join(code),
    onSuccess: (competition) => {
      analytics.track(AnalyticsEvent.CompetitionJoined, { competitionId: competition.id });
      queryClient.invalidateQueries({ queryKey: ["competitions"] });
      close();
    },
    onError: (error) => trackApiError(error, "join_competition"),
  });

  function close() {
    setCode("");
    mutation.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Join a competition"
      description="The admin of the competition will have sent you a code."
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            loading={mutation.isPending}
            disabled={code.trim().length < 4}
            onClick={() => mutation.mutate()}
          >
            Join competition
          </Button>
        </div>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="space-y-4"
      >
        <Input
          label="Invite code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="K7PQR2"
          required
          minLength={4}
          maxLength={12}
          autoFocus
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          className="tabular h-14 text-center text-xl font-bold tracking-[0.35em]"
        />

        {mutation.error && (
          <ErrorNote>
            {mutation.error instanceof ApiError
              ? mutation.error.message
              : "Couldn't join that competition."}
          </ErrorNote>
        )}
      </form>
    </Modal>
  );
}
